import CircularProgress from "@mui/material/CircularProgress";
import { useCallback, useEffect } from "react";
import { Virtuoso } from "react-virtuoso";
import { useDispatch, useSelector } from "react-redux";
import {
  selectPagedAnimations,
  selectSelectedAnimation,
  selectSelectedAnimationDetails,
  setSelectedAnimation,
  unloadedAnimation,
} from "../../../app/slices/AnimationsSlice";
import PiButton from "../../../components/PiButton";
import PiInputField from "../../../components/PiInputField";
import PiModal, { PiModalProps } from "../../../components/PiModal";
import { AnimationDetailsDTO } from "../../../dto/AnimationDetailsDTO";
import useAnimationLibrary from "./AnimationLibrary.hook";
import AnimationLibraryCard from "./AnimationLibraryCard";
import AnimationLibraryDetailsColumn from "./AnimationLibraryDetailsColumn";
import useAnimationLibraryPagination from "./AnimationLibraryPagination.hook";

export default function AnimationLibrary(props: PiModalProps) {
  const { onClose } = props;
  const dispatch = useDispatch();
  const {
    handleRangeChanged,
    setFilter,
    filter,
    handleRefresh,
    isRefreshing,
  } = useAnimationLibraryPagination();
  useAnimationLibrary();

  const pagedAnimations = useSelector(selectPagedAnimations);
  const selectedAnimation = useSelector(selectSelectedAnimation);
  const selectedDetails = useSelector(selectSelectedAnimationDetails);

  useEffect(() => {
    const firstLoadedAnimation = pagedAnimations.find(
      (animation) => animation.name !== unloadedAnimation.name
    );
    if (!firstLoadedAnimation) return;

    const hasValidSelection = pagedAnimations.some(
      (animation) =>
        animation.name !== unloadedAnimation.name &&
        animation.name === selectedAnimation?.name
    );
    if (hasValidSelection) return;

    dispatch(setSelectedAnimation(firstLoadedAnimation));
  }, [dispatch, pagedAnimations, selectedAnimation?.name]);

  const onSubmit = () => {
    if (!selectedDetails) return;
    props.onSubmit?.(selectedDetails as AnimationDetailsDTO);
    onClose();
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && props.onSubmit) {
        event.preventDefault();
        onSubmit();
      }
    },
    [props.onSubmit, onClose, selectedDetails]
  );

  return (
    <PiModal {...props}>
      <div
        className="flex h-full w-full gap-[16px] overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        <div className="min-w-0 flex-[1.75]">
          <AnimationLibraryDetailsColumn onSubmit={onSubmit} />
        </div>
        <div className="relative flex w-[360px] shrink-0 flex-col gap-[12px] border-l-2 border-solid border-creator-border pl-[16px]">
          <div className="flex w-full gap-[8px] pr-[8px]">
            <PiInputField
              showSearchIcon
              onChange={setFilter}
              label=""
              placeholder="Search"
              autoFocus
              showCrossIcon
              value={filter}
            />
            <div className="h-[36px] w-[100px]">
              <PiButton
                variant="piBlueOutlined"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                Refresh
              </PiButton>
            </div>
          </div>
          {isRefreshing && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <CircularProgress />
            </div>
          )}
          <div className="flex items-center justify-between pr-[8px] text-[13px] font-bold uppercase tracking-[0.08em] text-creator-text-sub">
            <span>All Animations</span>
            <span>{pagedAnimations.length}</span>
          </div>
          <Virtuoso
            className="pit-creator-scrollbar"
            totalCount={pagedAnimations.length}
            rangeChanged={handleRangeChanged}
            itemContent={(index) => (
              <AnimationLibraryCard
                key={index}
                animation={pagedAnimations[index]}
                onSubmit={onSubmit}
              />
            )}
            style={{ height: "100%", width: "100%" }}
          />
        </div>
      </div>
    </PiModal>
  );
}
